/**
 * useShotRecorder — the MediaRecorder machine for CLIP shots (FSD §4.2): 480p-first stream
 * (the camera hook picks the constraints), `timeslice ≈ 2000 ms` so chunks flow to the hash chain
 * and the background uploader WHILE the clip is still recording, auto-stop at the script's
 * maxDurationS, and an honest `unsupported` phase where MediaRecorder does not exist.
 *
 * Every `dataavailable` chunk is handed to `onChunk` with the wall-clock ISO timestamp taken at
 * delivery — that string is the X-SV-Captured-At header and is bound into the chain verbatim.
 *
 * Browser note (stated, not hidden): Safari may deliver a single blob at stop rather than honour
 * the timeslice. The chain and the upload still work (one chunk = the whole clip); only the
 * upload-while-recording benefit is lost. Telemetry records the chunk count so the be can see it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderPhase = 'idle' | 'recording' | 'stopping' | 'stopped' | 'unsupported' | 'error';

export interface RecorderChunk {
  readonly blob: Blob;
  readonly capturedAt: string;
  /** 1-based delivery index — becomes the chunk `seq`. */
  readonly index: number;
}

export interface RecorderStopInfo {
  readonly durationS: number;
  readonly chunkCount: number;
  readonly mimeType: string;
  /** US-03482 G4: the bytes every `dataavailable` delivered for this take (empty events count as nothing). */
  readonly totalBytes: number;
}

export interface ShotRecorderOptions {
  readonly timesliceMs?: number;
  readonly maxDurationS: number;
  readonly videoBitsPerSecond?: number;
  readonly onChunk: (chunk: RecorderChunk) => void;
  readonly onStop: (info: RecorderStopInfo) => void;
  readonly onError?: (message: string) => void;
}

export const DEFAULT_TIMESLICE_MS = 2000;
export const DEFAULT_VIDEO_BPS = 1_200_000;

const PREFERRED_TYPES = [
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=avc1',
  'video/mp4',
];

export function isMediaRecorderSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder === 'function';
}

/** The first container/codec this browser will record; null when none is (→ unsupported). */
export function pickMimeType(): string | null {
  if (!isMediaRecorderSupported()) return null;
  const MR = window.MediaRecorder as typeof MediaRecorder & { isTypeSupported?: (t: string) => boolean };
  if (typeof MR.isTypeSupported !== 'function') return 'video/webm';
  for (const t of PREFERRED_TYPES) if (MR.isTypeSupported(t)) return t;
  return null;
}

export interface ShotRecorderApi {
  readonly phase: RecorderPhase;
  readonly elapsedMs: number;
  readonly mimeType: string | null;
  readonly start: (stream: MediaStream, opts: ShotRecorderOptions) => void;
  readonly stop: () => void;
  readonly reset: () => void;
  /** G4: bytes delivered so far for the current take — read again after the last `dataavailable` lands. */
  readonly bytesRecorded: () => number;
}

export function useShotRecorder(): ShotRecorderApi {
  const [phase, setPhase] = useState<RecorderPhase>(() =>
    isMediaRecorderSupported() ? 'idle' : 'unsupported',
  );
  const [elapsedMs, setElapsed] = useState(0);
  const [mimeType, setMime] = useState<string | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const startedAt = useRef(0);
  const chunkIndex = useRef(0);
  const totalBytes = useRef(0);
  const tick = useRef<number | null>(null);
  const autoStop = useRef<number | null>(null);
  const disposed = useRef(false);

  const clearTimers = useCallback(() => {
    if (tick.current !== null) window.clearInterval(tick.current);
    if (autoStop.current !== null) window.clearTimeout(autoStop.current);
    tick.current = null;
    autoStop.current = null;
  }, []);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      clearTimers();
      const r = rec.current;
      if (r && r.state !== 'inactive') {
        try {
          r.stop();
        } catch {
          /* already stopped */
        }
      }
      rec.current = null;
    };
  }, [clearTimers]);

  const stop = useCallback(() => {
    const r = rec.current;
    if (!r || r.state === 'inactive') return;
    setPhase('stopping');
    clearTimers();
    try {
      r.stop();
    } catch {
      /* the onstop handler still fires for a recorder that was recording */
    }
  }, [clearTimers]);

  const start = useCallback(
    (stream: MediaStream, opts: ShotRecorderOptions) => {
      if (!isMediaRecorderSupported()) {
        setPhase('unsupported');
        return;
      }
      const type = pickMimeType();
      if (!type) {
        setPhase('unsupported');
        return;
      }
      let r: MediaRecorder;
      try {
        r = new MediaRecorder(stream, {
          mimeType: type,
          videoBitsPerSecond: opts.videoBitsPerSecond ?? DEFAULT_VIDEO_BPS,
        });
      } catch {
        // Some browsers reject a codec they claimed to support with a bitrate hint — retry bare.
        try {
          r = new MediaRecorder(stream);
        } catch (e) {
          setPhase('error');
          opts.onError?.(e instanceof Error ? e.message : 'recorder failed to start');
          return;
        }
      }
      rec.current = r;
      chunkIndex.current = 0;
      totalBytes.current = 0;
      const actualType = r.mimeType || type;
      setMime(actualType);

      r.ondataavailable = (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return;
        totalBytes.current += e.data.size;
        chunkIndex.current += 1;
        opts.onChunk({ blob: e.data, capturedAt: new Date().toISOString(), index: chunkIndex.current });
      };
      r.onerror = () => {
        if (disposed.current) return;
        clearTimers();
        setPhase('error');
        opts.onError?.('recording error');
      };
      r.onstop = () => {
        clearTimers();
        const durationS = Math.max(0, (Date.now() - startedAt.current) / 1000);
        rec.current = null;
        if (!disposed.current) setPhase('stopped');
        opts.onStop({ durationS, chunkCount: chunkIndex.current, mimeType: actualType, totalBytes: totalBytes.current });
      };

      startedAt.current = Date.now();
      setElapsed(0);
      setPhase('recording');
      r.start(opts.timesliceMs ?? DEFAULT_TIMESLICE_MS);
      tick.current = window.setInterval(() => {
        if (!disposed.current) setElapsed(Date.now() - startedAt.current);
      }, 250);
      autoStop.current = window.setTimeout(() => stop(), Math.max(1, opts.maxDurationS) * 1000);
    },
    [clearTimers, stop],
  );

  const reset = useCallback(() => {
    clearTimers();
    rec.current = null;
    chunkIndex.current = 0;
    totalBytes.current = 0;
    setElapsed(0);
    setPhase(isMediaRecorderSupported() ? 'idle' : 'unsupported');
  }, [clearTimers]);

  const bytesRecorded = useCallback(() => totalBytes.current, []);

  return { phase, elapsedMs, mimeType, start, stop, reset, bytesRecorded };
}
